import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, ResultCard } from "./pipe-helpers";

/* ═══════════════════════════════════════════════════════
   PRESSURE DROP CALCULATOR
   Full pipe run with fittings via equivalent length method
   ═══════════════════════════════════════════════════════ */

interface PipeMaterial {
  label: string;
  c: number; // Hazen-Williams C
  sizes: { name: string; id: number }[];
}

const MATERIALS: PipeMaterial[] = [
  {
    label: "Steel Sch 40", c: 120,
    sizes: [
      { name: '1/2"', id: 0.622 }, { name: '3/4"', id: 0.824 }, { name: '1"', id: 1.049 },
      { name: '1-1/4"', id: 1.380 }, { name: '1-1/2"', id: 1.610 }, { name: '2"', id: 2.067 },
      { name: '2-1/2"', id: 2.469 }, { name: '3"', id: 3.068 }, { name: '4"', id: 4.026 },
      { name: '6"', id: 6.065 }, { name: '8"', id: 7.981 }, { name: '10"', id: 10.020 }, { name: '12"', id: 12.000 },
    ],
  },
  {
    label: "Copper Type L", c: 140,
    sizes: [
      { name: '1/2"', id: 0.545 }, { name: '3/4"', id: 0.785 }, { name: '1"', id: 1.025 },
      { name: '1-1/4"', id: 1.265 }, { name: '1-1/2"', id: 1.505 }, { name: '2"', id: 1.985 },
      { name: '2-1/2"', id: 2.465 }, { name: '3"', id: 2.945 }, { name: '4"', id: 3.905 },
    ],
  },
  {
    label: "PVC Sch 40", c: 150,
    sizes: [
      { name: '1/2"', id: 0.622 }, { name: '3/4"', id: 0.824 }, { name: '1"', id: 1.049 },
      { name: '1-1/4"', id: 1.380 }, { name: '1-1/2"', id: 1.610 }, { name: '2"', id: 2.067 },
      { name: '3"', id: 3.068 }, { name: '4"', id: 4.026 }, { name: '6"', id: 6.065 },
    ],
  },
];

interface FittingCount {
  name: string;
  eqLengthFactor: number; // multiplier × nominal pipe size for eq. length
  count: number;
}

const FITTING_TEMPLATES: { name: string; eqLengthFactor: number }[] = [
  { name: "90° Elbow", eqLengthFactor: 30 },
  { name: "45° Elbow", eqLengthFactor: 16 },
  { name: "Tee (thru)", eqLengthFactor: 20 },
  { name: "Tee (branch)", eqLengthFactor: 60 },
  { name: "Gate Valve", eqLengthFactor: 8 },
  { name: "Globe Valve", eqLengthFactor: 340 },
  { name: "Ball Valve", eqLengthFactor: 3 },
  { name: "Check Valve", eqLengthFactor: 100 },
  { name: "Butterfly Valve", eqLengthFactor: 45 },
];

export const PressureDropCalculator = () => {
  const [matIdx, setMatIdx] = useState("0");
  const [sizeIdx, setSizeIdx] = useState("3");
  const [flowGPM, setFlowGPM] = useState("");
  const [straightLength, setStraightLength] = useState("");
  const [fittings, setFittings] = useState<FittingCount[]>(
    FITTING_TEMPLATES.map((f) => ({ ...f, count: 0 }))
  );

  const mat = MATERIALS[parseInt(matIdx)];
  const pipe = mat.sizes[parseInt(sizeIdx)] ?? mat.sizes[0];
  const Q = parseFloat(flowGPM);
  const Ls = parseFloat(straightLength);
  const hasInput = Q > 0 && Ls > 0;

  const updateFitting = (idx: number, count: number) => {
    setFittings((prev) => prev.map((f, i) => (i === idx ? { ...f, count: Math.max(0, count) } : f)));
  };

  const results = useMemo(() => {
    if (!hasInput) return null;

    const d = pipe.id;
    const C = mat.c;

    // Equivalent length of fittings (L/D method): eqLength = factor × (d / 12)
    const totalEqLength = fittings.reduce((sum, f) => sum + f.count * f.eqLengthFactor * (d / 12), 0);
    const totalLength = Ls + totalEqLength;

    // Hazen-Williams
    const lossPerHundred = (4.52 * Math.pow(Q, 1.85)) / (Math.pow(C, 1.85) * Math.pow(d, 4.87));
    const totalLoss = lossPerHundred * (totalLength / 100);
    const straightLoss = lossPerHundred * (Ls / 100);
    const fittingLoss = totalLoss - straightLoss;
    const velocity = (0.4085 * Q) / (d * d);
    const headLoss = totalLoss * 2.31;

    return {
      totalLength,
      eqLength: totalEqLength,
      lossPerHundred,
      totalLoss,
      straightLoss,
      fittingLoss,
      velocity,
      headLoss,
    };
  }, [hasInput, Q, Ls, pipe, mat, fittings]);

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate <span className="text-foreground font-semibold">total pressure drop</span> through a complete pipe run including fittings using the equivalent length method.
        </p>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Material</label>
            <Select value={matIdx} onValueChange={(v) => { setMatIdx(v); setSizeIdx("0"); }}>
              <SelectTrigger className="bg-card border-border font-mono-data text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{MATERIALS.map((m, i) => (<SelectItem key={i} value={String(i)}>{m.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Size</label>
            <Select value={sizeIdx} onValueChange={setSizeIdx}>
              <SelectTrigger className="bg-card border-border font-mono-data text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{mat.sizes.map((s, i) => (<SelectItem key={i} value={String(i)}>{s.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Flow (GPM)</label>
            <Input type="number" inputMode="decimal" placeholder="GPM…" value={flowGPM} onChange={(e) => setFlowGPM(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Straight Length (ft)</label>
            <Input type="number" inputMode="decimal" placeholder="Feet…" value={straightLength} onChange={(e) => setStraightLength(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
      </div>

      {/* Fitting counts */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Fittings & Valves</h3>
        <div className="grid grid-cols-1 gap-1.5">
          {fittings.map((f, i) => (
            <div key={f.name} className="flex items-center justify-between bg-card border border-border rounded-lg px-3 py-2">
              <span className="text-xs text-foreground font-medium">{f.name}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => updateFitting(i, f.count - 1)}
                  className="w-7 h-7 rounded-md bg-secondary text-foreground font-bold text-sm flex items-center justify-center hover:bg-secondary/80">−</button>
                <span className="w-6 text-center font-mono-data text-sm font-bold text-foreground">{f.count}</span>
                <button onClick={() => updateFitting(i, f.count + 1)}
                  className="w-7 h-7 rounded-md bg-secondary text-foreground font-bold text-sm flex items-center justify-center hover:bg-secondary/80">+</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {results && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Total Pressure Drop" value={fmt(results.totalLoss, 2)} unit="PSI" accent />
          <ResultCard label="Straight Pipe Loss" value={fmt(results.straightLoss, 2)} unit="PSI" />
          <ResultCard label="Fitting Loss" value={fmt(results.fittingLoss, 2)} unit="PSI" />
          <ResultCard label="Head Loss" value={fmt(results.headLoss, 2)} unit="ft head" />
          <ResultCard label="Loss per 100 ft" value={fmt(results.lossPerHundred, 3)} unit="PSI/100ft" />
          <ResultCard label="Equivalent Length (fittings)" value={fmt(results.eqLength, 1)} unit="ft" />
          <ResultCard label="Total Equivalent Length" value={fmt(results.totalLength, 1)} unit="ft" />
          <ResultCard label="Flow Velocity" value={fmt(results.velocity, 2)} unit="ft/s" />

          {results.velocity > 8 && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
              <p className="text-xs text-destructive font-semibold">⚠️ Velocity exceeds 8 ft/s — risk of erosion and water hammer.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
